import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../App';
import { api } from '../lib/api';
import { signOutNow } from '../lib/firebase';
import { Button, Card, ErrorBanner, Field, Input, Modal, Select, Empty } from '../components/ui';
import { date } from '../lib/format';

/** Company picker, and the only place a company gets created. */
export default function Companies() {
  const { profile, reloadProfile } = useSession();
  const [creating, setCreating] = useState(false);

  const companies = profile?.companies ?? [];

  return (
    <div className="full-page" style={{ alignItems: 'flex-start', paddingTop: 64 }}>
      <div style={{ width: '100%', maxWidth: 720 }}>
        <div className="page-head">
          <div>
            <h1>Your companies</h1>
            <p>Signed in as {profile?.user?.email}</p>
          </div>
          <div className="page-actions">
            <Button variant="primary" onClick={() => setCreating(true)}>
              New company
            </Button>
            <Button onClick={() => signOutNow()}>Sign out</Button>
          </div>
        </div>

        {companies.length === 0 ? (
          <Card>
            <Empty
              title="No companies yet"
              action={
                <Button variant="primary" onClick={() => setCreating(true)}>
                  Create your first company
                </Button>
              }
            >
              Creating a company makes you its owner. Everything else — share classes,
              stakeholders, issuances — hangs off it.
            </Empty>
          </Card>
        ) : (
          <div className="stack">
            {companies.map((company) => (
              <Link key={company.id} to={`/companies/${company.id}`} className="company-card">
                <div>
                  <div className="company-card-name">{company.name}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {String(company.entity_type ?? '').replace(/_/g, '-')} · {company.currency} ·
                    member since {date(company.member_since)}
                  </div>
                </div>
                <span className="badge badge-info">{String(company.role).toLowerCase()}</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {creating ? (
        <CreateCompanyModal
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await reloadProfile();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateCompanyModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    legalName: '',
    entityType: 'C_CORP',
    jurisdiction: 'DE',
    incorporationDate: '',
    currency: 'USD',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // Strip empties — the API's schemas treat absent and blank differently.
      const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ''));
      await api.createCompany(body);
      await onCreated();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  const fieldErrors = error?.fieldErrors ?? {};

  return (
    <Modal
      title="New company"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Create company
          </Button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Name" required error={fieldErrors.name}>
            <Input value={form.name} onChange={set('name')} required autoFocus />
          </Field>
          <Field label="Legal name" hint="If different from the trading name">
            <Input value={form.legalName} onChange={set('legalName')} />
          </Field>
          <Field label="Entity type">
            <Select value={form.entityType} onChange={set('entityType')}>
              {['C_CORP', 'S_CORP', 'LLC', 'LP', 'PLC', 'OTHER'].map((t) => (
                <option key={t} value={t}>
                  {t.replace(/_/g, '-')}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Jurisdiction" hint="e.g. DE">
            <Input value={form.jurisdiction} onChange={set('jurisdiction')} />
          </Field>
          <Field label="Incorporation date" error={fieldErrors.incorporationDate}>
            <Input type="date" value={form.incorporationDate} onChange={set('incorporationDate')} />
          </Field>
          <Field label="Currency">
            <Input value={form.currency} onChange={set('currency')} maxLength={3} />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
